import { Product, Variant } from '@shared/types/product';
import { Attribute, Product as EntityProduct, ProductCarousel, ProductImage } from '@/types/entity/product';
import { Currency } from '@/types/currency';
import { Cart } from '@shared/types/cart';
import { Locale } from '@/project.config';
import {
  parseCtLifecycleCodes,
  isStockDueSoonEligibleFromCodes,
  resolveBadgeFromLifecycleCodes,
  isRemovedFromSaleForLocale,
} from '@/utils/product/lifecycle';
import { deriveCartonQuantity } from '@/utils/product/parse-carton-quantity';
import { mapCategory } from './map-category';
import { mapAttribute } from './map-attribute';
import { mapProductFeaturesToSpecifications } from './map-product-features';
import { mapProductBarcodes } from './map-product-barcodes';
import { derivePackSize } from './derive-pack-size';
import { normalizeBrandName } from './normalize-brand-name';

export const mapProduct = (
  product: Product,
  {
    variantIndex = -1,
    cart,
    locale = 'en-AU',
    filterOutNonMatchingVariants = true,
  }: { variantIndex?: number; cart?: Cart; locale?: Locale; filterOutNonMatchingVariants?: boolean } = {},
): EntityProduct => {
  const variant =
    product.variants[variantIndex] ??
    product.variants.find(
      (v) =>
        !!v.discountedPrice?.value?.centAmount &&
        !!v.price?.centAmount &&
        v.discountedPrice.value.centAmount < v.price?.centAmount,
    ) ??
    product.variants[0];

  const cheapestVariant = product.variants.reduce(
    (a, b) => ((a.price?.centAmount ?? Number.MAX_VALUE) < (b.price?.centAmount ?? Number.MAX_VALUE) ? a : b),
    {
      price: { centAmount: Number.MAX_VALUE },
    } as Variant,
  );

  const mostExpensiveVariant = product.variants.reduce(
    (a, b) => ((a.price?.centAmount ?? Number.MIN_VALUE) > (b.price?.centAmount ?? Number.MIN_VALUE) ? a : b),
    {
      price: { centAmount: Number.MIN_VALUE },
    } as Variant,
  );

  const colors = product.variants
    .filter((variant) => (filterOutNonMatchingVariants ? variant.isMatchingVariant !== false : true))
    .map((v) => mapAttribute(v.attributes?.color))
    .filter(Boolean) as Attribute[];
  //   Temporary until correct mapping is done for the model attribute
  const specs = product.variants
    .filter((variant) => (filterOutNonMatchingVariants ? variant.isMatchingVariant !== false : true))
    .map((v) => mapAttribute({ label: v.attributes?.model, key: v.attributes?.model }))
    .filter(Boolean) as Attribute[];

  const priceRange =
    cheapestVariant &&
    mostExpensiveVariant &&
    cheapestVariant.price?.centAmount !== mostExpensiveVariant.price?.centAmount
      ? ([
          (cheapestVariant.price?.centAmount ?? 0) / Math.pow(10, cheapestVariant.price?.fractionDigits ?? 2),
          (mostExpensiveVariant.price?.centAmount ?? 0) / Math.pow(10, mostExpensiveVariant.price?.fractionDigits ?? 2),
        ] as [number, number])
      : undefined;

  const inCartQuantity = cart?.lineItems?.find((lineItem) => lineItem.variant?.sku === variant.sku)?.count ?? 0;

  // EWM-194 / EWM-195 / EWM-192 — info-panel + lifecycle attributes.
  //
  // BFF prefix convention (verified against electus-dev SB2486 + RR3369):
  // every CT variant attribute is re-keyed with a `variants.attributes.`
  // prefix when the BFF maps it onto the FE-facing variant
  // (see `backend/.../ProductMapper.generateAttributeIdFromAttributeName`).
  // So `productBrand` from CT lands at `variants.attributes.productBrand`.
  //
  // Many "complex" CT attributes are stored as JSON-encoded STRINGS that
  // need parsing on the FE side (the BFF doesn't deep-parse them).
  // `parseJsonAttr` handles that pattern.
  const variantAttributes = (variant.attributes ?? {}) as Record<string, unknown>;
  const getAttr = (name: string): unknown => variantAttributes[`variants.attributes.${name}`];
  const parseJsonAttr = (name: string): unknown => {
    const raw = getAttr(name);
    if (typeof raw !== 'string' || !raw) return undefined;
    try {
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  };

  // EWM-327 — `productBrand` comes in three shapes (verified live on
  // electus-dev across 8,924 products): a plain string ("POWERTECH SLA"),
  // a JSON envelope `{"brandInfo":{"name":...}}` (~8%, e.g. TS1314=PORTASOL),
  // or an empty string (~62%). EWM-194 only handled the plain-string case
  // after sampling 3 plain-string SKUs, so the ~8% with the envelope rendered
  // no brand. `normalizeBrandName` resolves every shape to the display name
  // and treats empty / missing / malformed as absent (row stays hidden).
  const brand = normalizeBrandName(getAttr('productBrand'));

  // EWM-194 — `mpn` is a plain string (often empty). Treat empty as absent.
  const mpnRaw = getAttr('mpn');
  const mpn = typeof mpnRaw === 'string' && mpnRaw.length > 0 ? mpnRaw : undefined;

  // EWM-192 — `productLifeCycle` is a JSON-encoded array of per-company
  // lifecycle entries: `[{ company, values: { lifeCycleAttribute, ... } }, ...]`.
  // Codes are conventional Jaycar/Electus single letters: "C" = Current,
  // "Z" / "Zed" = Discontinued. Treat the product as discontinued if
  // ANY company entry says "Z" / "Zed". Per-BU filtering (so an "active
  // in jrau but Z in sdau" product appears active for jrau dealers only)
  // is a refinement for the tastic layer when BU context is available.
  const lifecycleCodes = parseCtLifecycleCodes(variantAttributes);
  const isDiscontinued = lifecycleCodes.some((code) => code === 'Z' || code === 'Zed');
  // EWM-360 / EWM-643 — `GO` = removed from sale (Luke 2026-06-13). GO is
  // PER-COUNTRY (a product can be pulled in NZ yet stay on sale in AU), so
  // this flag is scoped to THIS site's company entry via the shared region
  // engine — the same parser + rule the BFF's ProductApi gate uses, so the
  // FE and the backend can never disagree. (The earlier any-company `.some()`
  // wrongly disabled AU Add-to-Cart when only NZ was GO.) The aggregated
  // `lifecycleCodes` above intentionally remain cross-company for badges and
  // stock-due-soon.
  // EWM-944 — via the shared helper, so `isRemovedFromSaleForLocale`'s claim to
  // use "the same helper and rule as map-product" is true rather than an
  // identical expression inlined in two files.
  const isRemovedFromSale = isRemovedFromSaleForLocale(variantAttributes, locale);
  // EWM-545 — `N` (new) / `P` (pending) lifecycle products are new arrivals
  // awaiting their first stock; when out of stock they read "Stock due soon".
  const isStockDueSoonEligible = isStockDueSoonEligibleFromCodes(lifecycleCodes);

  // EWM-194 — extract the CT `productReplacements` references. CT stores
  // this as `[{ typeId: 'product', id: '<uuid>' }, ...]` per variant
  // (verified against SB2486 → SB2487 on electus-dev) and can hold multiple
  // entries.
  //
  // The BFF's `ProductMapper.extractAttributeValue` flattens product-reference
  // values to their bare ID string, so the FE receives a plain `string[]` of
  // UUIDs (not the raw `{typeId,id}` envelope). Older revisions of this file
  // tried to JSON-parse the value, which silently dropped the data — read the
  // array directly here. The Object-of-`{id}` shape is kept as a fallback in
  // case a future BFF revision passes the raw CT envelope through.
  //
  // The PDP tastic resolves the IDs into the rich
  // `supersededBy { name, sku, url }` shape via a second BFF call — see
  // `useSupersededBy`. When the attribute is absent / empty, the Superseded
  // alert simply doesn't render.
  const replacementsRaw = getAttr('productReplacements');
  const replacementsList = Array.isArray(replacementsRaw) ? (replacementsRaw as unknown[]) : [];
  const replacementIds = replacementsList
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        const id = (entry as { id?: unknown }).id;
        if (typeof id === 'string') return id;
      }
      return undefined;
    })
    .filter((id): id is string => typeof id === 'string' && id.length > 0);

  // EWM-195 / EWM-210 — RRP per variant. Primary source is a CT
  // Standalone Price on the `price-rrp` channel for this SKU + currency
  // (resolved on the BFF in ProductApi.fetchStandalonePricesForSkus).
  // Secondary fallback is a CT variant custom attribute, currently
  // unused in electus-dev. Convert Money → dollars for the pricing-card
  // RRP pill. Absent on both sources → pill hides.
  const rrpMoney = variant.rrp;
  const rrp =
    typeof rrpMoney?.centAmount === 'number'
      ? rrpMoney.centAmount / Math.pow(10, rrpMoney.fractionDigits ?? 2)
      : undefined;

  // EWM-195 / EWM-210 — bulk-pricing tiers. Sourced from CT's `tiers[]`
  // on the dealer's Standalone Price (price-trade / price-wholesale /
  // etc., per the dealer's BU distributionChannelId). Map to the entity
  // shape consumed by the pricing-card Bulk Pricing accordion. EWM-316 —
  // the accordion renders a 2-col Quantity / Price layout for all products
  // and customer types, so no `unit` label is mapped.
  const priceBreaks = variant.priceBreaks?.map((tier) => ({
    quantity: tier.minimumQuantity,
    price: (tier.value?.centAmount ?? 0) / Math.pow(10, tier.value?.fractionDigits ?? 2),
  }));

  // EWM-315 — pack-size label from the CT `freightAttributes` attribute
  // (a JSON-encoded string; confirmed live on QM1500 / WH3098, en-AU + en-NZ).
  // `derivePackSize` returns undefined when the data is incomplete, so the
  // PDP "Pack Size" row hides gracefully (matching brand / MPN behaviour).
  const freightAttributes = parseJsonAttr('freightAttributes');
  const packSize = derivePackSize(freightAttributes);

  // EWM-743 — carton quantity (OneOffice TU5) is a key INSIDE that same
  // freight payload (`outer_tu5item_quantity`, confirmed by Leo 2026-09-09),
  // not a top-level attribute, so both rows come from one parse. Absent on
  // all but a handful of products today, which correctly hides the row.
  const cartonQuantity = deriveCartonQuantity(freightAttributes);

  // The following entity fields exist on `Product` but have no CT source
  // wired today; their rendering branches hide gracefully until backend
  // tickets land:
  //   - badge variants other than       → EWM-209 (PDP lifecycle-badge rewire
  //     `on-special`                       not yet wired; CLEARANCE / NEW)

  // EWM-195 — full SKU-level variant list for the dropdown. Built from every
  // CT variant on the master product so the dropdown can show all options
  // even when the project's color/specs pickers don't apply.
  // EWM-212 — `downloads` is a JSON-encoded `{ downloads: [{ url, name, type, altText }, ...] }`
  // envelope. `url` is a bare filename served from the Jaycar media CDN at
  // `https://media.jaycar.com.au/product/resources/<file>` (documents) or
  // `/images/<file>` (images). `type` ("MSDS" / "Datasheet") is the visible
  // button label until the spec author confirms human-readable mapping.
  const MEDIA_BASE = 'https://media.jaycar.com.au/product';
  const RESOURCES_URL = `${MEDIA_BASE}/resources/`;
  const IMAGES_URL = `${MEDIA_BASE}/images/`;

  // EWM-193 — `productCarousalImage` (sic: misspelled in CT) is a JSON-encoded
  // `{ carousel: { 3dimageID, imageList: [{ url, altText }, ...], dimensions } }`
  // envelope. `url` is a bare filename served at `${IMAGES_URL}<file>`. The CT
  // `masterVariant.images` field only carries the thumbnail (`_thm.jpg`,
  // 560×560); the full-resolution carousel images (1440×1440) live here.
  // `3dimageID` is captured for future 3D-viewer work (empty string today on
  // every probed product). Verified against electus-dev (PA3503 = 1 image;
  // SB2486 = 2; RR3369 = 1).
  const carouselParsed = parseJsonAttr('productCarousalImage') as
    | {
        carousel?: {
          '3dimageID'?: unknown;
          imageList?: Array<{ url?: unknown; altText?: unknown }>;
        };
      }
    | undefined;
  const carouselNode = carouselParsed?.carousel;
  const carouselImageList = Array.isArray(carouselNode?.imageList) ? carouselNode.imageList : undefined;
  const imageList: ProductImage[] | undefined = carouselImageList
    ?.map((entry) => {
      const rawUrl = typeof entry.url === 'string' && entry.url ? entry.url : undefined;
      if (!rawUrl) return undefined;
      const url = /^https?:\/\//.test(rawUrl) ? rawUrl : `${IMAGES_URL}${rawUrl}`;
      const altText = typeof entry.altText === 'string' && entry.altText ? entry.altText : undefined;
      return altText ? { url, altText } : { url };
    })
    .filter((img): img is ProductImage => !!img);
  const threeDImageIdRaw = carouselNode?.['3dimageID'];
  const threeDImageId =
    typeof threeDImageIdRaw === 'string' && threeDImageIdRaw.length > 0 ? threeDImageIdRaw : undefined;
  const carousel: ProductCarousel | undefined =
    imageList && imageList.length > 0 ? (threeDImageId ? { imageList, threeDImageId } : { imageList }) : undefined;

  // EWM-198 — `productFeatures` is a JSON-encoded array of category groups,
  // each with a `features: [{ name, value, unit, featureposition, ... }]`
  // list. The helper handles multivalued aggregation + Physical_PROD /
  // Prod_PACKAGED L×H×D synthesis. Replaces the previous `mapMasterAttributes`
  // source which dumped every variant attribute (including JSON blobs like
  // the carousel envelope) as raw rows. Verified against electus-dev
  // (HP1222, SB2486, WC7766, PA3503).
  const specifications: Attribute[] = mapProductFeaturesToSpecifications(parseJsonAttr('productFeatures'));

  // EWM-936 — the CT `barcodes` attribute is a JSON-encoded
  // `{ ean_barcode, upc_barcode, additional_barcodes }` envelope. Kept as its
  // own typed field rather than folded into `specifications`: barcode rows are
  // signed-in-only, and this mapper has no auth context. `parseJsonAttr`
  // already yields `undefined` for the `"testbarcodes"` placeholder that 4,174
  // of 8,928 dev products carry, so those simply produce no barcodes.
  const barcodes = mapProductBarcodes(parseJsonAttr('barcodes'));

  const downloadsParsed = parseJsonAttr('downloads');
  const downloadEntries: Array<Record<string, unknown>> | undefined = Array.isArray(downloadsParsed)
    ? (downloadsParsed as Array<Record<string, unknown>>)
    : downloadsParsed &&
        typeof downloadsParsed === 'object' &&
        Array.isArray((downloadsParsed as { downloads?: unknown }).downloads)
      ? (downloadsParsed as { downloads: Array<Record<string, unknown>> }).downloads
      : undefined;

  const downloads = downloadEntries
    ?.map((entry) => {
      const type = typeof entry.type === 'string' ? entry.type : undefined;
      const rawUrl = typeof entry.url === 'string' ? entry.url : undefined;
      if (!type || !rawUrl) return undefined;
      const url = /^https?:\/\//.test(rawUrl) ? rawUrl : `${RESOURCES_URL}${rawUrl}`;
      return { label: type, url };
    })
    .filter((d): d is { label: string; url: string } => !!d);

  // Cross-product family variant dropdown. When the BFF populates
  // `familyVariants` (products sharing the same `productFamilyCode`
  // attribute), show siblings as dropdown options that redirect on select.
  // Each entry's `url` triggers a redirect instead of a within-product
  // variant switch. Format matches `buildLabel` in VariantSelector:
  //   name (product name) + description (key) → "{name} — {key} — {price}"
  const familyVariantsList =
    product.familyVariants && product.familyVariants.length > 1
      ? product.familyVariants.map((fv) => {
          const fractionDigits = fv.price?.fractionDigits ?? 2;
          const price = (fv.price?.centAmount ?? 0) / Math.pow(10, fractionDigits);
          const discountedCents = fv.discountedPrice?.value?.centAmount;
          const discountedPrice =
            typeof discountedCents === 'number' ? discountedCents / Math.pow(10, fractionDigits) : undefined;
          const key = fv.key ?? fv.sku;
          return {
            sku: fv.sku,
            name: fv.name ?? key,
            description: key,
            price,
            ...(typeof discountedPrice === 'number' ? { discountedPrice } : {}),
            inStock: !!fv.isOnStock,
            ...(fv.slug ? { url: `/${fv.slug}/p/${fv.sku}` } : {}),
          };
        })
      : undefined;

  // Within-product SKU-level variant dropdown. Falls through whenever
  // the cross-product `familyVariants` list isn't populated (the common
  // case: colour / spec variants like `TL4980` + `TL4980-AMS` + `TL4980-OB`
  // share a product but differ at variant level). No `url` is set so
  // the selector triggers `onChange(sku)` → `usePDPProduct.onChangeSKU`
  // instead of redirecting. Price comes from the (CT-enriched) variant
  // price; SKUs without a Standalone Price land here with `price: 0`
  // and the VariantSelector renders "Unavailable" per the strict rule.
  const withinProductVariantsList =
    !familyVariantsList && product.variants.length > 1
      ? product.variants.map((v) => {
          const fractionDigits = v.price?.fractionDigits ?? 2;
          const price = (v.price?.centAmount ?? 0) / Math.pow(10, fractionDigits);
          const discountedCents = v.discountedPrice?.value?.centAmount;
          const discountedPrice =
            typeof discountedCents === 'number' ? discountedCents / Math.pow(10, fractionDigits) : undefined;
          const key = v.key ?? v.sku;
          return {
            sku: v.sku,
            name: product.name ?? key,
            description: key,
            price,
            ...(typeof discountedPrice === 'number' ? { discountedPrice } : {}),
            inStock: !!v.isOnStock,
          };
        })
      : undefined;

  const variantsList = familyVariantsList ?? withinProductVariantsList;

  const discount = variant.discountedPrice?.discount;
  const discountValue = discount?.discountValue;

  let discountType: 'relative' | 'absolute' | undefined;
  let discountAmount: number | undefined;

  if (discountValue) {
    if (discountValue.type === 'relative') {
      discountType = 'relative';
      discountAmount = discountValue.value / 100;
    } else if (discountValue.type === 'absolute') {
      discountType = 'absolute';
      discountAmount = (discountValue.value?.centAmount ?? 0) / 100;
    }
  }

  // EWM-657 — derive the marketing badge from the `productLifeCycle` code(s),
  // the same source + precedence the PLP/search path uses (shared
  // `resolveBadgeFromLifecycleCodes`), so a product shows the identical badge on
  // its PDP as on the PLP. (Replaces the old discount-only `on-special` signal,
  // which never surfaced CLEARANCE / NEW here.)
  const badge = resolveBadgeFromLifecycleCodes(lifecycleCodes);

  return {
    id: product.productId ?? '',
    key: product.productKey,
    ref: product.productRef,
    sku: variant.sku,
    name: product.name ?? '',
    description: product.description,
    specifications,
    // EWM-936 — always mapped; the PDP gates the rows on sign-in.
    barcodes,
    images: variant.images,
    colors,
    specs,
    price: (variant.price?.centAmount ?? 0) / Math.pow(10, variant.price?.fractionDigits ?? 2),
    // Only set discountedPrice when CT actually returned one. The previous
    // fallback to `0` made every undiscounted product look 100%-off in the
    // tile (PriceBlock then renders 0 as the main price + the real price
    // as RRP, since `isDiscounted = discountedPrice < price` is true for 0).
    ...(typeof variant.discountedPrice?.value?.centAmount === 'number'
      ? {
          discountedPrice: variant.discountedPrice.value.centAmount / Math.pow(10, variant.price?.fractionDigits ?? 2),
        }
      : {}),
    currency: (variant.price?.currencyCode ?? 'USD') as Currency,
    inStock: variant.isOnStock,
    maxQuantity: variant.isOnStock ? Math.max(0, (variant.availableQuantity ?? Number.MAX_VALUE) - inCartQuantity) : 0,
    ...(priceRange ? { priceRange } : {}),
    url: product._url,
    categories: product.categories?.map((c) => mapCategory(c, { locale })) ?? [],
    ...(product.primaryCategoryId ? { primaryCategoryId: product.primaryCategoryId } : {}),
    ...(brand ? { brand } : {}),
    ...(mpn ? { mpn } : {}),
    ...(packSize ? { packSize } : {}),
    ...(cartonQuantity !== undefined ? { cartonQuantity } : {}),
    ...(badge ? { badge } : {}),
    ...(replacementIds.length > 0 ? { supersededByRef: { ids: replacementIds } } : {}),
    ...(isDiscontinued ? { isDiscontinued } : {}),
    ...(isRemovedFromSale ? { isRemovedFromSale } : {}),
    ...(isStockDueSoonEligible ? { isStockDueSoonEligible } : {}),
    ...(typeof rrp === 'number' ? { rrp } : {}),
    ...(priceBreaks && priceBreaks.length > 0 ? { priceBreaks } : {}),
    ...(variantsList ? { variants: variantsList } : {}),
    ...(carousel ? { carousel } : {}),
    ...(downloads && downloads.length > 0 ? { downloads } : {}),
    ...(discount
      ? {
          discount: {
            name: discount.name ?? '',
            description: discount.description ?? '',
            type: discountType,
            value: discountAmount,
          },
        }
      : {}),
  };
};
